import React from "react";
import { Box, Checkbox, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from "@mui/material";
import { headCellSx, bodyCellSx } from "./listing";
import { tokens, radius } from "../theme";
import TableSkeleton from "./TableSkeleton";
import EmptyState from "./EmptyState";
import { useRovingRows } from "./useRovingRows";

/**
 * A listing, with all four of its states.
 *
 * Fifty-three screens in this product hand-rolled a `<Table>` while six used the
 * shared one, and the reason was that the shared one only covered the happy
 * path: a bare string for "no records" and no failure state at all. So every
 * screen grew its own `loading ? … : error ? … : empty ? … : <Table>` ladder,
 * and every one of them drifted a little — which is how a table ends up with
 * its own copy of the head-cell style and its own status colours.
 *
 * This owns the ladder instead. The four states are the module-screens book's:
 * loading renders the real header with skeleton rows beneath it, so nothing
 * shifts when the data lands; empty and failed replace the table entirely;
 * failed is never red, because a read that did not work is not the reader's
 * mistake.
 *
 * Columns are declared rather than rendered by the caller, which is what makes
 * the header, the widths, the alignment and the skeleton agree without anyone
 * restating them:
 *
 *   <DataTable
 *     columns={[
 *       { key: "name",   label: "Name",   width: "26%", render: (r) => <Person row={r} /> },
 *       { key: "amount", label: "Amount", align: "right", render: (r) => money(r.total) },
 *     ]}
 *     rows={filtered}
 *     loading={loading}
 *     error={failed}
 *     onRetry={load}
 *     empty={{ title: "No people yet", description: "Add your first." }}
 *     onRowClick={(r) => navigate(`/app/people/${r._id}`)}
 *   />
 */
const DataTable = ({
  columns = [],
  rows = [],
  getRowId = (row, i) => row?._id ?? i,
  loading,
  error,
  onRetry,
  errorTitle = "Couldn't load this list",
  empty,
  onRowClick,
  // optional selection; omit `selected` entirely for a table without checkboxes
  selected,
  onToggleRow,
  onToggleAll,
  tableSx,
  // A summary row inside the table's own shell — a selection total, a column
  // sum. It belongs within the border rather than under it: a total that floats
  // below the card is a separate statement, not the bottom of the column it
  // totals.
  footer,
  // Per-row styling, as a function of the row. Used by exactly one screen: an
  // over-policy expense claim tints its whole background, which the book calls
  // out as the one place in PayLoom where a row does.
  rowSx,
}) => {
  const { bodyRef, rowProps } = useRovingRows();
  const selectable = Boolean(selected && onToggleRow);

  const allSelected = selectable && rows.length > 0 && rows.every((r, i) => selected.has(getRowId(r, i)));
  const someSelected = selectable && !allSelected && rows.some((r, i) => selected.has(getRowId(r, i)));

  const head = (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell sx={{ ...headCellSx, width: "3rem" }} padding="checkbox">
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={onToggleAll}
              inputProps={{ "aria-label": "Select all rows" }}
            />
          </TableCell>
        )}
        {columns.map((c) => (
          <TableCell
            key={c.key}
            align={c.align}
            sx={{ ...headCellSx, ...(c.width ? { width: c.width } : {}), ...c.headSx }}
          >
            {c.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  /**
   * The table's own shell.
   *
   * The book draws a list as a self-contained box: a hairline border, the
   * frame's card radius, clipped corners, and the header band inside it. The
   * shell is a card — a band on the page — so it takes 14, not the 10 it had:
   * 10 is the radius of a panel *inside* a card. That band is the
   * page blush, which only reads as a band when the table stands on the card
   * ground — on the page it is blush on blush and disappears.
   *
   * It belongs here rather than in each screen. Timesheets looked right only
   * because it hand-built a card around its table and People did not, which is
   * precisely the kind of difference a shared component exists to remove.
   */
  const Shell = ({ children }) => (
    <Box
      sx={{
        background: tokens.card,
        border: `1px solid ${tokens.border}`,
        borderRadius: `${radius.card}px`,
        overflow: "hidden",
        // The last row's own hairline would otherwise sit against the shell's.
        "& tbody tr:last-of-type td": { borderBottom: "none" },
      }}
    >
      {children}
    </Box>
  );

  // The header and the column widths are known before the data is, so they
  // render immediately and nothing shifts when the rows arrive.
  if (loading) {
    return (
      <Shell>
        <Table aria-busy="true" sx={tableSx}>
          {head}
          <TableBody>
            <TableSkeleton columns={columns.length + (selectable ? 1 : 0)} />
          </TableBody>
        </Table>
      </Shell>
    );
  }

  // Failed and empty replace the table: there is no data to put a header on,
  // and a header over nothing reads as a table that lost its rows.
  if (error) return <EmptyState variant="error" title={errorTitle} onRetry={onRetry} />;
  if (!rows.length) return <EmptyState {...(empty || { title: "Nothing here yet" })} />;

  return (
    <Shell>
    <Table sx={tableSx}>
      {head}
      <TableBody ref={bodyRef}>
        {rows.map((row, i) => {
          const id = getRowId(row, i);
          return (
            <TableRow
              key={id}
              hover={Boolean(onRowClick)}
              selected={selectable && selected.has(id)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              {...rowProps(i)}
              sx={{
                ...(onRowClick ? { cursor: "pointer" } : {}),
                ...(rowSx ? rowSx(row, i) : {}),
              }}
            >
              {selectable && (
                // Stops the row's own click: ticking a box is not opening the
                // thing, and the two are a pixel apart.
                <TableCell padding="checkbox" sx={bodyCellSx} onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    size="small"
                    checked={selected.has(id)}
                    onChange={() => onToggleRow(id)}
                    inputProps={{ "aria-label": "Select row" }}
                  />
                </TableCell>
              )}
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align} sx={{ ...bodyCellSx, ...c.cellSx }}>
                  {c.render ? c.render(row, i) : <Box component="span">{row?.[c.key] ?? "—"}</Box>}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
      {footer && (
        <TableFooter>
          <TableRow sx={{ background: tokens.surface }}>{footer}</TableRow>
        </TableFooter>
      )}
    </Table>
    </Shell>
  );
};

export default DataTable;
